import { Expense, ItemSplit, SplitTypeEnum, Tax } from "@finway-group/shared/lib/models"

import { ExpenseKindEnum } from "@finway-group/shared/lib/models"
import { Alert, Button, Col, Dropdown, Form, Input, Menu, Row, Select, Space } from "antd"
import { FormInstance } from "antd/lib/form"
import CurrencyInput from "Components/currencyInput"
import { ExpenseRules } from "Components/forms/rules"
import ExpenseInitializer from "Features/pages/expenses/expenseInitializer"
import { doTotalAndInlineAmountsMismatch, isHospitalityExpense, syncTotalExpenseAmountsToItems } from "Features/pages/expenses/expenseUtils"
import React, { useEffect } from "react"
import { Link as LinkIcon, MoreHorizontal as DotsIcon, Plus as PlusIcon, Trash2 as TrashIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { NO_TAX_RATE } from "Shared/config/consts"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { getPopupAnchor } from "Shared/utils"
import { getTax, getTaxLabel, roundNumberTo2Decimals } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { ItemSplitKindEnum } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { useDebouncedCallback } from "use-debounce/lib"
import { HoverableInfo } from "../reimbursementForms/auxilliary/HoverableInfo"
import DeletedTooltip from "Components/deletedTooltip"

interface TotalAmountFormInterface {
    expense: Expense
    updateExpense: (data: any) => void
    isLinked: boolean
    setIsLinked: (linked: boolean) => void
    isNew: boolean
    expenseForm: FormInstance
    forceSync?: boolean // Forces the total amount form to not be adjustable, and have to adjust through items
}

const TotalAmountForm: React.FC<TotalAmountFormInterface> = ({ expense, updateExpense, isLinked, setIsLinked, isNew, expenseForm, forceSync = false }) => {
    const { t } = useTranslation()
    const taxValues = useTaxes()
    const amountsMismatch = doTotalAndInlineAmountsMismatch(expense, taxValues, isNew)
    const { totalNetPrice, name } = ExpenseRules()
    const isHospitality = isHospitalityExpense(expense)
    const taxes = useTaxes()

    // when forceSync is true, where changes in the items MUST affect the total amounts to keep them synced at all times.
    const [shouldRecalculateAmounts, setShouldRecalculateAmounts] = useStateIfMounted(false)

    const recalculateAmountsCallback = useDebouncedCallback(() => setShouldRecalculateAmounts(true), 200)

    const isSubscription = expense.kind === ExpenseKindEnum.SUBSCRIPTION

    const onAddItem = (addItemAtTheBeginning = false) => {
        let splits
        if (addItemAtTheBeginning) {
            splits = [ExpenseInitializer.initializeNewItemSplit(taxValues), ...expense.splits]
        } else {
            splits = [...expense.splits, ExpenseInitializer.initializeNewItemSplit(taxValues)]
        }
        updateExpense({
            splits,
            splitType: SplitTypeEnum.ITEM,
        })
    }

    const onSelect = ({ key, domEvent }: any) => {
        domEvent.stopPropagation()
        switch (key) {
            case "add_item":
                onAddItem()
                break
            case "delete_all_items":
                updateExpense({ splits: [] })
                break
            case "link":
                setIsLinked(!isLinked)
                break
        }
    }

    const dropDownMenu = () => (
        <Menu onClick={onSelect}>
            {!isSubscription && (
                <>
                    {expense.splitType === SplitTypeEnum.SPLIT || expense.splits.length === 0 || (isHospitality && expense.splits.length === 1) ? (
                        <Menu.Item key="add_item" className="flex items-center min-w-190 py-8">
                            <PlusIcon size={12} />
                            <span className="ml-6">{t("action:request.item.add")}</span>
                        </Menu.Item>
                    ) : (
                        !isHospitality && (
                            <Menu.Item key="delete_all_items" className="flex items-center min-w-190 py-8">
                                <TrashIcon size={12} />
                                <span className="ml-6">{t("action:request.delete_all_items")}</span>
                            </Menu.Item>
                        )
                    )}
                </>
            )}
            <Menu.Item key="link" className="flex items-center min-w-190 py-8">
                <LinkIcon size={12} />
                <span className="ml-6">{t(`action:request.${isLinked ? "unlink" : "link"}_fields`)}</span>
            </Menu.Item>
        </Menu>
    )

    const updateItemSplit = (index: number, updateClosure: any) => {
        updateExpense({ splits: expenseForm.getFieldValue("splits").map((split: ItemSplit, i: number) => (i === index ? { ...split, ...updateClosure(split) } : split)) })
        if (forceSync && expense.splits?.length > 0 && expense.splitType === SplitTypeEnum.ITEM) {
            recalculateAmountsCallback.callback()
        }
    }

    const onNameChange = useDebouncedCallback((value: string, index: number) => {
        updateItemSplit(index, (_split: ItemSplit) => ({ name: value }))
    }, 800)

    const onTotalNetPriceUpdate = (value: number) => {
        const totalTaxPrice = roundNumberTo2Decimals(expense.taxRate !== NO_TAX_RATE ? (getTax(taxValues, expense.taxRate) / 100) * value : expense.totalTaxPrice)
        updateExpense({
            totalNetPrice: value,
            ...(isLinked ? { totalGrossPrice: value + totalTaxPrice, totalTaxPrice } : {}),
        })
    }

    const onTaxRateUpdate = (value: string) => {
        const tax = getTax(taxValues, value as string)
        const totalTaxValue = roundNumberTo2Decimals((tax / 100) * expense.totalNetPrice)
        updateExpense({
            taxRate: value,
            ...(isLinked ? { totalTaxPrice: totalTaxValue, totalGrossPrice: expense.totalNetPrice + totalTaxValue } : {}),
        })
    }

    const onTaxRateSplitUpdate = (index: number, value: string) => {
        updateItemSplit(index, (itemSplit: ItemSplit) => {
            const tax = getTax(taxValues, value as string)
            const taxValue = roundNumberTo2Decimals((tax / 100) * itemSplit.netPrice)

            return {
                taxRate: value,
                ...(isLinked ? { taxPrice: taxValue, grossPrice: itemSplit.netPrice + taxValue } : {}),
            }
        })
    }

    const onTotalTaxPriceUpdate = (value: number) => {
        // we unset the taxRate when we manually update the total tax price
        updateExpense({ totalTaxPrice: value, ...(isLinked ? { taxRate: NO_TAX_RATE, totalGrossPrice: roundNumberTo2Decimals(expense.totalNetPrice + value) } : {}) })
    }

    const onTotalGrossPriceUpdate = (value: number) => {
        if (!isLinked) {
            updateExpense({ totalGrossPrice: value })
            return
        }

        if (expense.taxRate !== NO_TAX_RATE) {
            const tax = getTax(taxValues, expense.taxRate)
            const totalNetValue = roundNumberTo2Decimals(value / (1 + tax / 100))
            updateExpense({ totalNetPrice: totalNetValue, totalTaxPrice: roundNumberTo2Decimals((tax / 100) * totalNetValue), totalGrossPrice: value })
        } else {
            const totalNetValue = roundNumberTo2Decimals(value - expense.totalTaxPrice)
            updateExpense({ totalNetPrice: totalNetValue, totalGrossPrice: value })
        }
    }

    const onNetPriceUpdate = (index: number, value: number) => {
        updateItemSplit(index, (itemSplit: ItemSplit) => {
            const taxPrice = roundNumberTo2Decimals(itemSplit.taxRate !== NO_TAX_RATE ? (getTax(taxValues, itemSplit.taxRate!) / 100) * value : itemSplit.taxPrice)

            return {
                netPrice: value,
                ...(isLinked ? { grossPrice: value + taxPrice, taxPrice } : {}),
            }
        })
    }

    const onTaxPriceUpdate = (index: number, value: number) => {
        // we unset the taxRate when we manually update the total tax price
        updateItemSplit(index, (itemSplit: ItemSplit) => ({
            taxPrice: value,
            ...(isLinked ? { taxRate: NO_TAX_RATE, grossPrice: roundNumberTo2Decimals(itemSplit.netPrice + value) } : {}),
        }))
    }

    const onGrossPriceUpdate = (index: number, value: number) => {
        updateItemSplit(index, (itemSplit: ItemSplit) => {
            if (!isLinked) return { grossPrice: value }

            if (itemSplit.taxRate !== NO_TAX_RATE) {
                const tax = getTax(taxValues, itemSplit.taxRate!)
                const netValue = roundNumberTo2Decimals(value / (1 + tax / 100))
                return { netPrice: netValue, taxPrice: roundNumberTo2Decimals((tax / 100) * netValue), grossPrice: value }
            } else {
                const netValue = roundNumberTo2Decimals(value - itemSplit.taxPrice)
                return { netPrice: netValue, grossPrice: value }
            }
        })
    }

    useEffect(() => {
        if (shouldRecalculateAmounts) {
            updateExpense(syncTotalExpenseAmountsToItems(expense, taxValues))
            setShouldRecalculateAmounts(false)
        }
    }, [shouldRecalculateAmounts])

    const addItemButton = (
        <Button
            onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation()
                isHospitality && onAddItem(isHospitality)
            }}
        >
            {!isHospitality ? <DotsIcon /> : <PlusIcon />}
        </Button>
    )

    return (
        <>
            <Row gutter={[16, 16]} align="middle">
                <Col span={6}>
                    <Form.Item name="totalNetPrice" label={t("input:request.total_net_amount")} rules={totalNetPrice} required>
                        <CurrencyInput
                            currency={expense.currency}
                            value={expense.totalNetPrice}
                            onChange={(_ev, value, _maskedValue) => onTotalNetPriceUpdate(value)}
                            disabled={forceSync}
                        />
                    </Form.Item>
                </Col>
                <Col span={5}>
                    <Form.Item name="taxRate" label={t("input:request.tax")} key="taxRate" initialValue={expense.taxRate || NO_TAX_RATE} required={expense.isAtLeastApproved()}>
                        <Select
                            className="w-full px-0 text-xs md:text-base no-padding-select"
                            dropdownStyle={{ minWidth: "150px", overflowX: "hidden" }}
                            dropdownMatchSelectWidth={false}
                            bordered={false}
                            getPopupContainer={getPopupAnchor()}
                            onChange={(value) => onTaxRateUpdate(value as string)}
                            disabled={forceSync}
                        >
                            {taxValues
                                .filter((tax: Tax) => !tax.deleted || tax._id == expense.taxRate)
                                .map((entry: Tax, index: number) => (
                                    <Select.Option disabled={entry.deleted} key={index} value={entry._id}>
                                        {entry.deleted && <DeletedTooltip translationKey="tooltips:vat.was_deleted" deletionDate={entry.dateDeleted} className="mr-4" />}
                                        {getTaxLabel(entry, true)}
                                    </Select.Option>
                                ))}
                            <Select.Option value={NO_TAX_RATE}>-</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={5}>
                    <Form.Item name="totalTaxPrice" label={t("input:request.total_tax")}>
                        <CurrencyInput
                            currency={expense.currency}
                            value={expense.totalTaxPrice}
                            onChange={(_ev, value, _maskedValue) => onTotalTaxPriceUpdate(value)}
                            disabled={forceSync}
                        />
                    </Form.Item>
                </Col>
                <Col span={1}></Col>
                <Col span={5}>
                    <Form.Item
                        name="totalGrossPrice"
                        label={
                            <>
                                {t("input:request.total_gross_amount")}
                                {isHospitality && <HoverableInfo message={t("tooltips:hospitality.total_calculated_amount")} type="info" />}
                            </>
                        }
                        required
                    >
                        <CurrencyInput currency={expense.currency} value={0} onChange={(_ev, value, _maskedValue) => onTotalGrossPriceUpdate(value)} disabled={forceSync} />
                    </Form.Item>
                </Col>
                {(!isHospitality || (isHospitality && expense.splits.length < 2)) && (
                    <Col span={1}>
                        {!isHospitality ? (
                            <Dropdown getPopupContainer={getPopupAnchor()} overlay={dropDownMenu} trigger={["click"]} className="flex items-center z-100">
                                {addItemButton}
                            </Dropdown>
                        ) : (
                            addItemButton
                        )}
                    </Col>
                )}
            </Row>
            {!isSubscription && (
                <>
                    <Form.List name="splits">
                        {(fields, { add, remove }) => {
                            return (
                                <div>
                                    {/* In oneTimeExpense form, we only want to show the item form if the expense split type is ITEM. */}
                                    {(!expense.splitType || expense.splitType === SplitTypeEnum.ITEM) &&
                                        fields.map((field, index) => {
                                            /**
                                             * If its a hospitality type expense, it can have a tip
                                             * This row will:
                                             * - not have the item name editable (fixed to "Tip"/"Trinkgeld")
                                             * - not have the tax amount editable
                                             */
                                            const isTipRow: boolean = (expense.splits[index] as ItemSplit)?.kind === ItemSplitKindEnum.TIP
                                            return (
                                                <Space key={index} className="flex mb-3" align="start">
                                                    <Row key={index} gutter={[16, 16]} align="top">
                                                        <Col span={6}>
                                                            <Form.Item
                                                                name={[field.name, "name"]}
                                                                label={t("input:request.item_name")}
                                                                key="itemName"
                                                                rules={name}
                                                                required={!isTipRow}
                                                            >
                                                                <Input
                                                                    placeholder={t("placeholder:request.item_name")}
                                                                    onChange={(e) => onNameChange.callback(e.target.value, index)}
                                                                    disabled={isTipRow}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={4}>
                                                            <Form.Item name={[field.name, "netPrice"]} label={t("input:request.net_price")} key="netPrice" required={!isTipRow}>
                                                                <CurrencyInput
                                                                    currency={expense.currency}
                                                                    value={0}
                                                                    onChange={(_ev, value, _maskedValue) => onNetPriceUpdate(index, value)}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        <Col span={4}>
                                                            <Form.Item
                                                                name={[field.name, "taxRate"]}
                                                                label={t("input:request.tax_rate")}
                                                                key="taxRate"
                                                                initialValue={taxValues[0]?._id}
                                                                required={!isTipRow && expense.isAtLeastApproved()}
                                                            >
                                                                <Select
                                                                    className="w-full text-xs md:text-base no-padding-select no-right-padding-select"
                                                                    dropdownMatchSelectWidth={false}
                                                                    dropdownStyle={{ minWidth: "150px", overflowX: "hidden" }}
                                                                    bordered={false}
                                                                    getPopupContainer={getPopupAnchor()}
                                                                    onChange={(value) => onTaxRateSplitUpdate(index, value as string)}
                                                                    disabled={isTipRow}
                                                                >
                                                                    {taxValues
                                                                        .filter((tax: Tax) => !tax.deleted || tax._id == expense.splits[index]?.taxRate)
                                                                        .map((entry: Tax, index: number) => (
                                                                            <Select.Option disabled={entry.deleted} key={index} value={entry._id}>
                                                                                {entry.deleted && (
                                                                                    <DeletedTooltip
                                                                                        className="mr-4 mb-2"
                                                                                        translationKey="tooltips:vat.was_deleted"
                                                                                        deletionDate={entry.dateDeleted}
                                                                                    />
                                                                                )}
                                                                                {getTaxLabel(entry, true)}
                                                                            </Select.Option>
                                                                        ))}
                                                                    <Select.Option value={NO_TAX_RATE}>-</Select.Option>
                                                                </Select>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={4}>
                                                            <Form.Item name={[field.name, "taxPrice"]} label={t("input:request.tax_price")} key="taxPrice">
                                                                <CurrencyInput
                                                                    currency={expense.currency}
                                                                    value={0}
                                                                    onChange={(_ev, value, _maskedValue) => onTaxPriceUpdate(index, value)}
                                                                    disabled={isTipRow}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        <Col span={4}>
                                                            <Form.Item
                                                                name={[field.name, "grossPrice"]}
                                                                label={t("input:request.gross_price")}
                                                                key="grossPrice"
                                                                required={!isTipRow}
                                                                rules={isTipRow ? totalNetPrice : []}
                                                            >
                                                                <CurrencyInput
                                                                    currency={expense.currency}
                                                                    value={0}
                                                                    onChange={(_ev, value, _maskedValue) => onGrossPriceUpdate(index, value)}
                                                                />
                                                            </Form.Item>
                                                        </Col>

                                                        <Col span={1}>
                                                            {fields.length > 0 && (
                                                                <Row>
                                                                    <Col>
                                                                        <Button
                                                                            className="mb-4"
                                                                            onClick={() => {
                                                                                remove(index)
                                                                                setShouldRecalculateAmounts(true)
                                                                                updateExpense({ splits: expense.splits.filter((_split, i) => i !== index) })
                                                                            }}
                                                                            disabled={isHospitality && expense.splits.length <= 1}
                                                                        >
                                                                            <TrashIcon />
                                                                        </Button>
                                                                    </Col>
                                                                </Row>
                                                            )}
                                                            {!isTipRow && (
                                                                <Row>
                                                                    <Col>
                                                                        <Button
                                                                            onClick={() => {
                                                                                const insertIndex = index + 1
                                                                                const newItem = ExpenseInitializer.initializeNewItemSplit(taxValues)
                                                                                add(newItem, insertIndex)
                                                                                const items = [...expense.splits]
                                                                                items.splice(insertIndex, 0, newItem)
                                                                                updateExpense({ splits: items })
                                                                            }}
                                                                        >
                                                                            <PlusIcon />
                                                                        </Button>
                                                                    </Col>
                                                                </Row>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </Space>
                                            )
                                        })}
                                    <Row></Row>
                                </div>
                            )
                        }}
                    </Form.List>
                    <>
                        {amountsMismatch && (
                            <Row gutter={[16, 16]} align="middle">
                                <Col span={24} sm="24" md="24">
                                    <Alert
                                        showIcon
                                        type="warning"
                                        message={
                                            <div>
                                                <span>{t("info:request_net_amount_mismatch")}</span>
                                                <Button
                                                    className="button-warning-notification ml-4"
                                                    type="text"
                                                    onClick={() => updateExpense(syncTotalExpenseAmountsToItems(expense, taxValues))}
                                                >
                                                    {t("action:request.sync_now")}
                                                </Button>
                                            </div>
                                        }
                                    />
                                </Col>
                            </Row>
                        )}
                    </>
                </>
            )}
        </>
    )
}

export default React.memo(TotalAmountForm)
